import { useEffect, useState } from "react";
import { useStyles } from "./styles";
import Link from "@mui/material/Link";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import { TextField } from "../components/TextField";
import { useMutation, useQuery, useQueryClient } from "react-query";
import * as Yup from "yup";
import {
  getInstances,
  postInstances,
  putInstance,
} from "../../../services/whatsapp";
import { useFormik } from "formik";
import { SaveButton } from "../../../components/SaveButton";
import { useToast } from "../../../hooks/useToast";
import { Instance, putInstanceObject } from "../../../types/whatsapp";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  //Checkbox,
  //FormControlLabel,
  //FormGroup,
  Grid,
  Typography,
} from "@mui/material";
import { Store } from "../../../types/stores";
import { QRCodeDialog } from "../../../components/QRCodeDialog";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";

interface WhatsappProps {
  store: Store;
  advance: (num: number) => void;
}

const WHATSAPP_SETTINGS_VALIDATION = Yup.object().shape({
  nome: Yup.string()
    .nullable()
    .required("Este campo é obrigatório")
    .matches(
      /^\s*\S.*$/,
      "Este campo não pode conter somente espaços em branco"
    )
    .min(1, "O nome da loja deve deve possuir pelo menos 1 caracteres"),
});

export function ConnectionWhatsapp(props: WhatsappProps) {
  const styles = useStyles();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const queryClient = useQueryClient();
  const [openQRCodeDialog, setOpenQRCodeDialog] = useState(false);
  const [instanceOnDialog, setInstanceOnDialog] = useState<Instance>({
    nome: "",
    id_loja: null,
    id: 1234,
    automatically_activate_contacts: 0,
    automatically_save_groups: 0,
    // host: "WHATTOAPI",
  });

  useEffect(() => {
    const params = {
      addAccount: "Whatsapp",
    };
    const options = {
      pathname: location.pathname,
      search: `?${createSearchParams(params)}`,
    };

    navigate(options, { replace: true });
  }, [location.pathname, navigate]);

  const formik = useFormik({
    initialValues: {} as Instance,
    onSubmit: (values) => onSubmit(values),
    validationSchema: WHATSAPP_SETTINGS_VALIDATION,
    validateOnChange: true,
  });

  const getInstancias = useQuery(
    "getInstancias",
    async () => {
      const response: any = await getInstances();
      if (response.length === 0) {
        let instancia = {
          nome: "Minha Instância",
          id_loja: props.store.id_loja,
          automatically_activate_contacts: 1,
          automatically_save_groups: 1,
        };

        createInstance(instancia);
      } else {
        if (response.length > 0) {
          var instances: Instance[] = [];

          for (let i = 0; i < response.length; i++) {
            if (response[i].id_loja === props.store.id_loja) {
              instances.push({ ...response[i] });

              formik.setValues({
                ...response[i],
                automatically_activate_contacts: 1,
                automatically_save_groups: 1,
              });

              formik.validateField("nome");
            }
          }

          if (instances.length === 0) {
            let instancia = {
              nome: "Minha Instância",
              id_loja: props.store.id_loja,
              automatically_activate_contacts: 1,
              automatically_save_groups: 1,
            };

            createInstance(instancia);
          } else {
            formik.setValues({
              ...response[0],
              automatically_activate_contacts: 1,
              automatically_save_groups: 1,
            });

            formik.validateField("nome");
            setInstanceOnDialog({
              ...instances[0],
              automatically_activate_contacts: 1,
              automatically_save_groups: 1,
            });
          }
        }
      }
      return response;
    },
    { staleTime: 2000, cacheTime: 10, enabled: !!props.store }
  );

  const createInstance = async (values: any) => {
    try {
      await postInstancia.mutateAsync(values);
    } catch (err) {
      console.log({ err });
    }
  };

  const postInstancia = useMutation(
    async (data: any) => {
      const response = await postInstances(data);
      return response;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("getInstancias");
      },
      onError: ({ response }) => {
        if (response?.data.code === 404) {
          if (response.data?.message === "Phone already exists") {
            toast.show("Não é permitido criar instâncias com o mesmo número!", {
              severity: "error",
            });
          }
        } else {
          toast.show("Falha ao criar a instancia!", { severity: "error" });
        }
        queryClient.invalidateQueries("getInstancias");
      },
    }
  );

  const postIntanciaUpdate = useMutation(
    async (instancia: Instance) => {
      const instanceCurrent: putInstanceObject = {
        ...instancia,
        name: instancia.nome,
        store: instancia.id_loja,
        id: instancia.id!,
        active: 1,
        save_contacts: 0,
        chat_duration: 12,
      };
      const response = await putInstance(instanceCurrent);
      return response;
    },
    {
      onSuccess: async () => {
        toast.show("Instancia atualizada com sucesso!", {
          severity: "success",
        });
        props.advance(2);
      },
      onError: () => {
        toast.show("Falha ao atualizar a instancia!", { severity: "warning" });
        props.advance(2);
      },
    }
  );

  const onSubmit = async (values: any) => {
    try {
      await postIntanciaUpdate.mutateAsync(values);
    } catch (err) {
      console.log({ err });
    }
  };

  return (
    <Grid container>
      <Grid xs={12} item className={styles.boxTitle}>
        <Grid>
          <Typography className={styles.titulo}>Conectar WhatsApp</Typography>
          <Typography className={styles.subtitulo}>
            É necessário conectar seu Whatsapp para enviar ofertas.
          </Typography>
          <Link
            href={"https://www.youtube.com/watch?v=zYz4kF_qTtc"}
            target={"_blank"}
            underline="none"
          >
            <Button
              variant="contained"
              sx={{
                color: "#ffffff",
                backgroundColor: "#0C20B5",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textAlign: "left",
                display: "block",
                marginTop: "22px",
              }}
            >
              Como configurar esta página?
            </Button>
          </Link>
        </Grid>
      </Grid>
      <Divider className={styles.firstDivider} />
      <Grid xs={12} item>
        {getInstancias.isSuccess && instanceOnDialog ? (
          <>
            <form noValidate onSubmit={formik.handleSubmit}>
              <Grid xs={12} item>
                <Grid
                  className={styles.boxTextField}
                  sx={{ flexDirection: "column" }}
                >
                  <TextField
                    label="Número do whatsapp"
                    value={formik.values.own_number}
                    error={!!formik.errors.own_number}
                    onChange={(evt: any, values: any) => {
                      formik.setFieldValue("own_number", evt.target.value);
                    }}
                    helperText={
                      "Escreva o número do whatsapp que desejar conectar"
                    }
                  />
                  <TextField
                    label="Nome da loja"
                    value={props.store.nome_loja}
                    disabled={true}
                    onChange={formik.handleChange}
                    helperText={"Loja na qual este número de whatsapp pertence"}
                  />
                  <TextField
                    label="Nome"
                    value={formik.values.nome}
                    error={!!formik.errors.nome}
                    onChange={(evt: any, values: any) => {
                      formik.setFieldValue("nome", evt.target.value);
                    }}
                    helperText={
                      "Dê um nome para identificar a quem pertence este número"
                    }
                  />
                </Grid>
              </Grid>
              {/*<Grid item xs={12} className={styles.boxCheckbox}>
                <FormGroup row>
                  <FormControlLabel
                    className={styles.labelChekBox}
                    control={
                      <Checkbox
                        name="automatically_activate_contacts"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          if (e.target.checked) {
                            formik.setFieldValue(
                              "automatically_activate_contacts",
                              1
                            );
                          } else {
                            formik.setFieldValue(
                              "automatically_activate_contacts",
                              0
                            );
                          }
                        }}
                        checked={
                          formik.values.automatically_activate_contacts === 0
                            ? false
                            : true
                        }
                      />
                    }
                    label="Salvar contatos"
                  />
                  <FormControlLabel
                    className={styles.labelChekBox}
                    control={
                      <Checkbox
                        name="automatically_save_groups"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          if (e.target.checked) {
                            formik.setFieldValue(
                              "automatically_save_groups",
                              1
                            );
                          } else {
                            formik.setFieldValue(
                              "automatically_save_groups",
                              0
                            );
                          }
                        }}
                        checked={
                          formik.values.automatically_save_groups === 0
                            ? false
                            : true
                        }
                      />
                    }
                    label="Salvar grupos"
                  />
                </FormGroup>
              </Grid>*/}
              <Grid item sx={{ marginTop: "1.5rem" }}>
                <Button
                  className={styles.buttonConect}
                  onClick={() => {
                    setOpenQRCodeDialog(true);
                  }}
                >
                  <QrCode2Icon sx={{ marginRight: "8px", color: "#616161" }} />
                  Conectar Whatsapp
                </Button>
              </Grid>
              <Divider className={styles.firstDivider} />
              <Grid item xs={12} className={styles.boxButtons}>
                <Button
                  className={styles.buttonPular}
                  onClick={() => {
                    props.advance(2);
                  }}
                >
                  Pular etapa
                </Button>
                <SaveButton
                  titulo="Continuar"
                  isSubmitting={formik.isSubmitting}
                  size="large"
                />
              </Grid>
            </form>
            <QRCodeDialog
              open={openQRCodeDialog}
              instance={instanceOnDialog}
              close={() => setOpenQRCodeDialog(false)}
            />
          </>
        ) : (
          <Box className={styles.boxCircular}>
            <CircularProgress variant="indeterminate" />
          </Box>
        )}
      </Grid>
    </Grid>
  );
}
